/* eslint-disable import/no-extraneous-dependencies */
import 'reflect-metadata';
import { view, View } from '@esui/core';
import { VSTack } from '@esui/core/layout/vstack.js';
import { navigate, Router } from '@esui/core/router/index.js';
import { Button } from '../button/index.js';
import { buttonIframeRoutes } from '../button/routes.js';
import { drawerIframeRoutes } from '../drawer/routes.js';
import { IconButton } from '../icon-button/index.js';
import { Icon } from '../icon/index.js';
import { List } from '../list/index.js';
import { ItemGraphic, ItemSecondaryLine, ListItem } from '../list/item.js';
import '../theme.js';
import { TopAppBar } from '../top-app-bar/index.js';

const defaultTitle = 'Material Web Components Catalog';

interface MenuItemOptions {
  title: string;
  description: string;
  icon: string;
  path: string;
}

function MenuItem({ title, description, icon, path }: MenuItemOptions) {
  return ListItem(
    title,
    ItemGraphic(Icon(icon)),
    ItemSecondaryLine(description)
  )
    .graphic('icon')
    .twoline()
    .value(path);
}

function BackHome() {
  return IconButton('arrow_back').onClick(() => navigate('/'));
}

export const [Main] = view('demo-main-component', {
  template() {
    function WithToAppBar(...content: View[]) {
      return TopAppBar(...content)
        .title(defaultTitle)
        .navigationIcon(Icon('home'));
    }

    return Router(
      {
        pattern: '(/)',
        async viewFactory() {
          return WithToAppBar(
            VSTack(
              List(
                MenuItem({
                  title: 'Button',
                  icon: 'add_circle_outline',
                  description: 'Raised and flat buttons',
                  path: '/button',
                }),

                MenuItem({
                  title: 'Checkbox',
                  icon: 'check_box',
                  description: 'Multi-selection controls',
                  path: '/checkbox',
                }),

                MenuItem({
                  title: 'Circular Progress',
                  icon: 'lens',
                  description: 'Fills from 0-100%, represented by an arc.',
                  path: '/circular-progress',
                }),

                MenuItem({
                  title: 'Dialog',
                  icon: 'preview',
                  description: 'Popup that gains user attention.',
                  path: '/dialog',
                }),

                MenuItem({
                  title: 'Drawer',
                  icon: 'meeting_room',
                  description: ' Navigation to provide access to destinations.',
                  path: '/drawer',
                }),

                MenuItem({
                  title: 'Floating action button',
                  icon: 'add_circle',
                  description: 'The primary action in an application.',
                  path: '/fab',
                }),

                MenuItem({
                  title: 'Formfield',
                  icon: 'settings',
                  description: 'Layout form fields with labels.',
                  path: '/formfield',
                }),

                MenuItem({
                  title: 'Icon',
                  icon: 'sentiment_satisfied',
                  description: 'Material design icons',
                  path: '/icon',
                }),

                MenuItem({
                  title: 'Icon Button',
                  icon: 'code',
                  description:
                    'Icon buttons allow users to take actions, and make choices, with a single tap.',
                  path: '/icon-button',
                }),

                MenuItem({
                  title: 'Icon Button Toggle',
                  icon: 'code',
                  description: 'Toggling icon states.',
                  path: '/icon-button-toggle',
                }),

                MenuItem({
                  title: 'Linear Progress',
                  icon: 'linear_scale',
                  description: 'Fills from 0% to 100%, represented by bars.',
                  path: '/linear-progress',
                }),

                MenuItem({
                  title: 'Select',
                  icon: 'list_alt',
                  description: 'Single option dropdown select menus',
                  path: '/select',
                }),

                MenuItem({
                  title: 'Snackbar',
                  icon: 'call_to_action',
                  description: 'Transient messages',
                  path: '/snackbar',
                }),

                MenuItem({
                  title: 'Tabs',
                  icon: 'tab',
                  description: 'Tabs with support for icon and text labels',
                  path: '/tabs',
                }),

                MenuItem({
                  title: 'Textarea',
                  icon: 'text_fields',
                  description: 'Multi line text input.',
                  path: '/textarea',
                }),

                MenuItem({
                  title: 'Textfield',
                  icon: 'text_fields',
                  description: 'Single line text input.',
                  path: '/textfield',
                })
              ).onAction(selected => {
                navigate(selected.value);
              })
            )
          );
        },
      },

      {
        pattern: '/button',
        async viewFactory() {
          const { ButtonDemo } = await import('../button/demo.js');
          return WithToAppBar(ButtonDemo())
            .navigationIcon(BackHome())
            .title('Button');
        },
      },

      ...buttonIframeRoutes,

      {
        pattern: '/button/iframe(/)',
        async viewFactory() {
          return VSTack(Button('inside iframe'))
            .justifyItems('center')
            .style('padding', '1rem');
        },
      },

      {
        pattern: '/checkbox',
        async viewFactory() {
          const { CheckboxDemo } = await import('../checkbox/demo.js');
          return WithToAppBar(CheckboxDemo())
            .navigationIcon(BackHome())
            .title('Checkbox');
        },
      },

      {
        pattern: '/circular-progress',
        async viewFactory() {
          const { CircularProgressDemo } = await import(
            '../circular-progress/demo.js'
          );
          return WithToAppBar(CircularProgressDemo())
            .navigationIcon(BackHome())
            .title('Circular Progress');
        },
      },

      {
        pattern: '/dialog',
        async viewFactory() {
          const { DialogDemo } = await import('../dialog/demo.js');
          return WithToAppBar(DialogDemo())
            .navigationIcon(BackHome())
            .title('Dialog');
        },
      },

      {
        pattern: '/drawer',
        async viewFactory() {
          const { DrawerDemo } = await import('../drawer/demo.js');
          return WithToAppBar(DrawerDemo())
            .navigationIcon(BackHome())
            .title('Drawer');
        },
      },

      ...drawerIframeRoutes,

      {
        pattern: '/fab',
        async viewFactory() {
          const { FabDemo } = await import('../fab/demo.js');
          return WithToAppBar(FabDemo())
            .navigationIcon(BackHome())
            .title('Fab');
        },
      },

      {
        pattern: '/formfield',
        async viewFactory() {
          const { FormfieldDemo } = await import('../formfield/demo.js');
          return WithToAppBar(FormfieldDemo())
            .navigationIcon(BackHome())
            .title('Formfield');
        },
      },

      {
        pattern: '/icon',
        async viewFactory() {
          const { IconDemo } = await import('../icon/demo.js');
          return WithToAppBar(IconDemo())
            .navigationIcon(BackHome())
            .title('Icon');
        },
      },

      {
        pattern: '/icon-button',
        async viewFactory() {
          const { IconButtonDemo } = await import('../icon-button/demo.js');
          return WithToAppBar(IconButtonDemo())
            .navigationIcon(BackHome())
            .title('Icon Button');
        },
      },

      {
        pattern: '/icon-button-toggle',
        async viewFactory() {
          const { IconButtonToggleDemo } = await import(
            '../icon-button-toggle/demo.js'
          );
          return WithToAppBar(IconButtonToggleDemo())
            .navigationIcon(BackHome())
            .title('Icon Button Toggle');
        },
      },

      {
        pattern: '/linear-progress',
        async viewFactory() {
          const { LinearProgressDemo } = await import(
            '../linear-progress/demo.js'
          );
          return WithToAppBar(LinearProgressDemo())
            .navigationIcon(BackHome())
            .title('Linear Progress');
        },
      },

      {
        pattern: '/select',
        async viewFactory() {
          const { SelectDemo } = await import('../select/demo.js');
          return WithToAppBar(SelectDemo())
            .navigationIcon(BackHome())
            .title('Select');
        },
      },

      {
        pattern: '/snackbar',
        async viewFactory() {
          const { SnackbarDemo } = await import('../snackbar/demo.js');
          return WithToAppBar(SnackbarDemo())
            .navigationIcon(BackHome())
            .title('Snackbar');
        },
      },

      {
        pattern: '/tabs',
        async viewFactory() {
          const { TabDemo } = await import('../tabbar/demo.js');
          return WithToAppBar(TabDemo())
            .navigationIcon(BackHome())
            .title('Tabs');
        },
      },
      {
        pattern: '/textarea',
        async viewFactory() {
          const { TextareaDemo } = await import('../textarea/demo.js');
          return WithToAppBar(TextareaDemo())
            .navigationIcon(BackHome())
            .title('Textarea');
        },
      },

      {
        pattern: '/textfield',
        async viewFactory() {
          const { TextfieldDemo } = await import('../textfield/demo.js');
          return WithToAppBar(TextfieldDemo())
            .navigationIcon(BackHome())
            .title('Textfield');
        },
      }
    )
      .fallback({
        async viewFactory() {
          return WithToAppBar()
            .navigationIcon(BackHome())
            .title('Nothing here!');
        },
      })
      .style('--mdc-icon-font', 'Material Icons Outlined')
      .theme({
        primaryColor: '#004ecb',
        secondaryColor: '#546e7a',
      });
  },
});
